import { useEffect, useState, useCallback } from "react";
import "./App.css";
import SecondLineRoutes from "./routes/SecondLine.routes";
import AuthPage from "./pages/auth/AuthPage";
import {
  IFirstLineRoutes,
  ISecondLineRoutes,
  ISupportLines,
  type IKitPostmessage,
} from "./App.interface";
import FirstLineRoutes from "./routes/FirstLine.routes";
import api, {
  setAuthToken,
  getAuthToken,
  setOnUnauthorized,
} from "./api/axios";

const FIRST_LINE_GROUP_ID = Number(import.meta.env.VITE_FIRST_LINE_GROUP_ID);
const SECOND_LINE_GROUP_ID = Number(import.meta.env.VITE_SECOND_LINE_GROUP_ID);
const ALLOWED_ORIGINS = import.meta.env.VITE_ALLOWED_ORIGINS.split(",");
const SKIP_ORIGIN_CHECK = import.meta.env.VITE_SKIP_ORIGIN_CHECK === "true";

function App() {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [line, setLine] = useState<ISupportLines>();
  const [scondLineRoute, setSecondLineRoute] = useState<ISecondLineRoutes>(
    ISecondLineRoutes.default,
  );
  const [firstLineRoute, setFirstLineRoute] = useState<IFirstLineRoutes>(
    IFirstLineRoutes.default,
  );
  const [leadId, setLeadId] = useState<number | null>(null);
  const [phone, setPhone] = useState<string | null>(null);

  // Проверка существующей сессии
  const checkAuth = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setIsAuthorized(false);
      setIsAuthChecked(true);
      return;
    }

    try {
      await api.get("/auth/verify");
      setIsAuthorized(true);
    } catch {
      setAuthToken(null);
      setIsAuthorized(false);
    } finally {
      setIsAuthChecked(true);
    }
  }, []);

  // Обработка успешной авторизации
  const handleLoginSuccess = useCallback((token: string) => {
    setAuthToken(token);
    setIsAuthorized(true);
    setIsAuthChecked(true);
  }, []);

  const handleUnauthorized = useCallback(() => {
    setAuthToken(null);
    setIsAuthorized(false);
  }, []);

  // Устанавливаем callback для обработки 401 ошибок
  useEffect(() => {
    setOnUnauthorized(handleUnauthorized);
  }, [handleUnauthorized]);

  const handleFirsLine = useCallback((event: IKitPostmessage) => {
    setLine(ISupportLines.firstLine);

    const taskType = event.data?.active_task_data?.task_type;
    const parsedLeadId = Number(
      event.data?.active_task_data?.VARIABLES?.lead_id,
    );
    const leadId =
      Number.isFinite(parsedLeadId) && parsedLeadId > 0 ? parsedLeadId : null;
    const phone = event.data?.active_task_data?.CUSTOMER?.customer_phones?.[0];
    console.log(`Task type: ${taskType}`);
    console.log(`Lead: ${leadId}`);
    console.log(`Phone number: ${phone}`);

    setLeadId(leadId ?? null);
    setPhone(phone ?? null);

    switch (taskType) {
      case "IM":
        if (!phone) {
          setFirstLineRoute(IFirstLineRoutes.default);
          break;
        }

        setFirstLineRoute(IFirstLineRoutes.chatForm);
        break;

      case "CALL":
        setFirstLineRoute(IFirstLineRoutes.callForm);
        break;

      default:
        setFirstLineRoute(IFirstLineRoutes.default);
        break;
    }
  }, []);

  const handleSecondLine = useCallback((event: IKitPostmessage) => {
    setLine(ISupportLines.secondLine);

    const scriptValue = event.data?.active_task_data?.VARIABLES?.script;

    if (scriptValue) {
      console.log(`Script: ${scriptValue}`);

      switch (scriptValue) {
        case "Перенос обучения":
          setSecondLineRoute(ISecondLineRoutes.reschedule);
          break;
        case "Успех":
          setSecondLineRoute(ISecondLineRoutes.success);
          break;
        case "Запланировано":
          setSecondLineRoute(ISecondLineRoutes.remider);
          break;
        case 'КИС "АРТ"':
          setSecondLineRoute(ISecondLineRoutes.newService);
          break;
        case "ИД":
          setSecondLineRoute(ISecondLineRoutes.accessibilityInterval);
          break;
        default:
          setSecondLineRoute(ISecondLineRoutes.default);
          break;
      }
    }
  }, []);

  useEffect(() => {
    checkAuth();

    const handleMessage = (event: IKitPostmessage) => {
      if (!SKIP_ORIGIN_CHECK && !ALLOWED_ORIGINS.includes(event.origin)) {
        console.warn("Wrong origin:", event.origin);
        return;
      }

      const groupId = event.data?.agent_data?.group_id;

      console.log(`Group: ${groupId}`);

      if (groupId === SECOND_LINE_GROUP_ID) handleSecondLine(event);
      if (groupId === FIRST_LINE_GROUP_ID) handleFirsLine(event);
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [checkAuth, handleFirsLine, handleSecondLine]);

  if (!isAuthChecked) {
    return null;
  }

  if (!isAuthorized) {
    return <AuthPage onLoginSuccess={handleLoginSuccess}></AuthPage>;
  }

  return (
    <div className="app-container">
      {line === ISupportLines.firstLine && (
        <FirstLineRoutes
          route={firstLineRoute}
          leadId={leadId}
          phone={phone}
        ></FirstLineRoutes>
      )}
      {line === ISupportLines.secondLine && (
        <SecondLineRoutes route={scondLineRoute}></SecondLineRoutes>
      )}
    </div>
  );
}

export default App;
